import signCareLogo from '@/assets/brand/signcare-logo.svg';
import {
  WelcomeConsentNotice,
  type WelcomePreviewStep,
  WelcomeScreen,
} from '@/features/workflows/components/simple/preview-screens';

import type { SessionDetailDto } from '../schemas/sessionSchemas';
import { toWelcomeSteps } from './welcomeSteps';

/**
 * The overview + its Continue button + the consent line — the complete first screen
 * of a run, as ONE definition.
 *
 * It is rendered in two places: before the OTP gate on the shared link
 * ({@link PublicWelcomeScreen} below) and inside the run itself
 * (`SessionMobilePanel`, for an admin filling a session in). Both must be the same
 * screen, so neither builds it itself — a copy in each would drift the moment the
 * button or the spacing changed on one side.
 */
export function WelcomeIntroBlock({
  steps,
  heading,
  subheading,
  onContinue,
}: {
  steps: WelcomePreviewStep[];
  heading?: string;
  subheading?: string;
  onContinue: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* `flex-1` makes the overview GROW to fill the card, so its list absorbs the
          spare height and the DPDP notice ends up directly above Continue. Without
          it the block hugs the top and the notice floats mid-card with a gap beneath
          — worst on a one-step workflow, where the spare height is largest.
          The logo is rendered by the host above this block, hence `hideLogo`. */}
      <WelcomeScreen
        className="min-h-0 flex-1"
        steps={steps}
        heading={heading}
        subheading={subheading}
        hideLogo
      />
      <button
        type="button"
        onClick={onContinue}
        className="mt-4 w-full border py-3 text-[length:var(--text-16)] font-medium"
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          color: 'var(--brand-btn-text, #fff)',
          borderRadius: 'var(--brand-btn-radius, 8px)',
        }}
      >
        Continue
      </button>
      <div className="mt-2.5">
        <WelcomeConsentNotice />
      </div>
    </div>
  );
}

/**
 * The FIRST screen of the shared link: what the applicant is about to be asked for,
 * shown before the mobile-number gate.
 *
 * It runs on the PROVISIONAL intro (GetPublicWorkflowIntro) — no session exists yet,
 * nothing is written, and Continue only reveals the OTP gate. Putting the overview
 * first means the applicant knows what the verification involves, and what data it
 * touches, BEFORE being asked to hand over a phone number.
 *
 * The card, background and logo placement mirror the live run's, so crossing the OTP
 * gate does not look like arriving at a different product.
 */
export function PublicWelcomeScreen({
  intro,
  onContinue,
}: {
  intro: SessionDetailDto;
  onContinue: () => void;
}) {
  return (
    // The `sc-run-*` classes are inert outside `.sc-session-preview-page` — they let
    // the entry screen's scoped CSS collapse this frame on a phone without affecting
    // anywhere else this block renders.
    <div className="sc-run-shell flex h-full w-full flex-col overflow-y-auto bg-[#F1F5FB] px-4 py-8 md:px-8">
      <div className="sc-run-column mx-auto w-full max-w-lg">
        <div className="sc-run-card flex min-h-[calc(100dvh-8rem)] flex-col rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <div className="mb-4 flex h-9 w-full shrink-0 items-center justify-center">
            <img
              src={intro.customization?.logoUrl || signCareLogo}
              alt="Brand"
              className="max-h-full max-w-[150px] object-contain"
            />
          </div>

          <WelcomeIntroBlock
            steps={toWelcomeSteps(intro.steps)}
            heading={intro.options?.welcomeHeading ?? undefined}
            subheading={intro.options?.welcomeSubheading ?? undefined}
            onContinue={onContinue}
          />
        </div>
      </div>
    </div>
  );
}
